import { format } from 'date-fns';
import { AlertCircle, CheckCircle, Clock, FileText, Loader2, Play, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { OrderDocument } from '../hooks/useDocuments';
import { useProcessDocument } from '../hooks/useDocuments';

interface DocumentCardProps {
  orderId: string;
  document: OrderDocument;
  onDeleted?: () => void;
}

function getStatusBadgeVariant(status: OrderDocument['processing_status']) {
  switch (status) {
    case 'pending':
      return 'secondary';
    case 'processing':
      return 'default';
    case 'validating':
      return 'secondary';
    case 'completed':
      return 'outline';
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getStatusIcon(status: OrderDocument['processing_status']) {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4" />;
    case 'processing':
      return <Loader2 className="h-4 w-4 animate-spin" />;
    case 'completed':
      return <FileText className="h-4 w-4 text-green-600" />;
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
}

function getStatusLabel(status: OrderDocument['processing_status']) {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'processing':
      return 'Processing';
    case 'validating':
      return 'Validating';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function DocumentCard({ orderId, document }: DocumentCardProps) {
  const processDocument = useProcessDocument();
  const navigate = useNavigate();

  const handleProcess = () => {
    processDocument.mutate({ documentId: document.id });
  };

  const handleValidate = () => {
    // Pass validation_session_id and orderId via state if available
    navigate(`/cd/${document.transaction_id}/documents/${document.id}/validate`, {
      state: { sessionId: document.validation_session_id, orderId },
    });
  };

  const canProcess =
    document.processing_status === 'pending' || document.processing_status === 'failed';
  // Only show Validate button when status is validating AND validation_session_id is present
  const canValidate =
    document.processing_status === 'validating' && !!document.validation_session_id;
  const isProcessingAction = processDocument.isPending;

  // Safely format the date, handling invalid dates
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Unknown date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, 'MMM d, yyyy • h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Card className="hover:border-gray-300 transition-colors w-[360px]">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              {getStatusIcon(document.processing_status)}
            </div>
            <div>
              <h4 className="font-medium text-sm max-w-[200px] truncate" title={document.file_name}>
                {document.file_name}
              </h4>
              <p className="text-xs text-gray-500">{formatDate(document.created_at)}</p>
            </div>
          </div>
          <Badge variant={getStatusBadgeVariant(document.processing_status)} className="text-xs">
            {getStatusLabel(document.processing_status)}
          </Badge>
        </div>

        {document.error_message && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
            <p className="text-xs text-red-700">{document.error_message}</p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase tracking-wide">
            {document.document_type === 'cd' ? 'Closing Disclosure' : 'Other Document'}
          </span>
          {canProcess && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleProcess}
              disabled={isProcessingAction}
              className="h-7 text-xs"
            >
              {isProcessingAction ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Processing...
                </>
              ) : document.processing_status === 'failed' ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </>
              ) : (
                <>
                  <Play className="h-3 w-3 mr-1" />
                  Process
                </>
              )}
            </Button>
          )}
          {/* Show "Preparing..." when validating but validation_session_id not yet available */}
          {document.processing_status === 'validating' && !document.validation_session_id && (
            <Button variant="outline" size="sm" disabled className="h-7 text-xs">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Preparing...
            </Button>
          )}
          {canValidate && (
            <Button variant="outline" size="sm" onClick={handleValidate} className="h-7 text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              Validate
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
